import { useEffect, useState } from 'react';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { globalSphereCache } from '@center-platform/spheres/cache';
import type { GlyphColor } from '../types/glyph';

export type SphereInfo = {
  id: string;
  label?: string;
  properties?: {
    name?: string;
    glyph?: string;
    glyphColor?: GlyphColor;
    imageId?: string;
    email?: string;
  };
};

/**
 * Hook to fetch sphere info with batching, deduplication, and caching.
 * Delegates to globalSphereCache so all sphere info lookups share
 * one cache and batch window, regardless of which component triggers them.
 */
export function useSphereInfo(id?: string) {
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;

  const [info, setInfo] = useState<SphereInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    if (!id || !graphClient) {
      setInfo(null);
      setLoading(false);
      return;
    }

    // Check synchronous cache first
    const cached = globalSphereCache.getCachedSphere(id);
    if (cached) {
      setInfo(cached.info as SphereInfo);
      setLoading(false);
      return;
    }

    setLoading(true);

    globalSphereCache.getSphereData(id, graphClient)
      .then((cachedData) => {
        if (cancelled) return;
        setInfo(cachedData.info as SphereInfo);
      })
      .catch(() => {
        if (!cancelled) setInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, graphClient]);

  return { info, loading };
}
